<template>
  <fieldset class="form-group" :class="groupClasses">
    <!-- Group legend/title -->
    <legend 
      v-if="title" 
      class="group-legend"
      :class="legendClasses"
    >
      {{ title }}
      <span v-if="required" class="required-marker" aria-label="Required">*</span>
    </legend>
    
    <!-- Group description -->
    <div v-if="description" class="group-description">
      {{ description }}
    </div>
    
    <!-- Group content -->
    <div class="group-content">
      <slot 
        :has-errors="hasErrors"
        :error-count="errorCount"
        :is-valid="isValid"
      />
    </div>
    
    <!-- Group-level errors -->
    <div v-if="groupErrors.length > 0" class="group-errors">
      <div 
        v-for="(error, index) in groupErrors" 
        :key="index" 
        class="group-error"
      >
        <Icon name="error" :size="12" class="error-icon" />
        {{ error }}
      </div>
    </div>
    
    <!-- Group help text -->
    <div v-if="helpText" class="group-help">
      <Icon name="info" :size="12" class="help-icon" />
      {{ helpText }}
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue';
import Icon from '@/components/atoms/Icon.vue';

export interface FormGroupProps {
  title?: string;
  description?: string;
  helpText?: string;
  required?: boolean;
  disabled?: boolean;
  layout?: 'vertical' | 'horizontal' | 'grid';
  columns?: number;
  spacing?: 'compact' | 'normal' | 'relaxed';
  size?: 'small' | 'medium' | 'large';
  // Validation props
  groupErrors?: string[];
  validateOnBlur?: boolean;
  validateOnInput?: boolean;
}

const props = withDefaults(defineProps<FormGroupProps>(), {
  required: false,
  disabled: false,
  layout: 'vertical',
  columns: 2,
  spacing: 'normal',
  size: 'medium',
  groupErrors: () => [],
  validateOnBlur: true,
  validateOnInput: false
});

const emit = defineEmits<{
  'validate': [isValid: boolean, errors: string[]];
}>();

// Track child field validation states
const childFieldStates = ref<Map<string, { isValid: boolean; errors: string[] }>>(new Map());

// Computed properties for validation state
const hasErrors = computed(() => 
  childFieldStates.value.size > 0 && 
  Array.from(childFieldStates.value.values()).some(state => !state.isValid)
);

const errorCount = computed(() => 
  Array.from(childFieldStates.value.values()).reduce((count, state) => 
    count + (state.isValid ? 0 : state.errors.length), 0
  )
);

const isValid = computed(() => 
  !hasErrors.value && props.groupErrors.length === 0
);

// Styling classes
const groupClasses = computed(() => [
  `form-group--${props.layout}`,
  `form-group--${props.spacing}`,
  `form-group--${props.size}`,
  {
    'form-group--required': props.required,
    'form-group--disabled': props.disabled,
    'form-group--error': hasErrors.value || props.groupErrors.length > 0,
    'form-group--grid': props.layout === 'grid'
  }
]);

const legendClasses = computed(() => ({
  'legend--required': props.required,
  'legend--disabled': props.disabled
}));

// Register/unregister child field validation states
const registerField = (fieldId: string, isValid: boolean, errors: string[]) => {
  childFieldStates.value.set(fieldId, { isValid, errors });
  emitValidationState();
};

const unregisterField = (fieldId: string) => {
  childFieldStates.value.delete(fieldId);
  emitValidationState();
};

const emitValidationState = () => {
  const allErrors = [
    ...props.groupErrors,
    ...Array.from(childFieldStates.value.values()).flatMap(state => state.errors)
  ];
  
  emit('validate', isValid.value, allErrors);
};

// Watch for changes in group errors
watch(() => props.groupErrors, emitValidationState, { deep: true });

// Provide context to child components
provide('formGroupContext', {
  registerField,
  unregisterField,
  layout: props.layout,
  spacing: props.spacing,
  size: props.size,
  disabled: props.disabled,
  validateOnBlur: props.validateOnBlur,
  validateOnInput: props.validateOnInput
});
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';

.form-group {
  border: none;
  margin: 0;
  padding: 0;
  width: 100%;
  
  &--small {
    font-size: 12px;
  }
  
  &--medium {
    font-size: var(--vscode-font-size);
  }
  
  &--large {
    font-size: 14px;
  }
  
  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
  
  &--error .group-legend {
    color: var(--vscode-inputValidation-errorForeground);
  }
}

.group-legend {
  font-weight: 600;
  color: var(--vscode-foreground);
  font-size: inherit;
  line-height: 1.4;
  margin-bottom: var(--spacing-sm);
  padding: 0;
  
  &--disabled {
    color: var(--vscode-foreground);
    opacity: 0.6;
  }
}

.required-marker {
  color: var(--vscode-inputValidation-errorForeground);
  margin-left: var(--spacing-xs);
  font-weight: normal;
}

.group-description {
  font-size: 12px;
  color: var(--vscode-foreground);
  opacity: 0.8;
  line-height: 1.4;
  margin-bottom: var(--spacing-md);
}

.group-content {
  display: flex;
  flex-direction: column;
  
  // Vertical layout (default)
  &.form-group--vertical & {
    gap: var(--spacing-lg);
  }
  
  // Horizontal layout
  &.form-group--horizontal & {
    flex-direction: row;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: var(--spacing-lg);
    
    > :deep(*) {
      flex: 1;
      min-width: 200px;
    }
  }
  
  // Grid layout
  &.form-group--grid & {
    display: grid;
    gap: var(--spacing-lg);
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
}

// Spacing variants
.form-group--compact .group-content {
  gap: var(--spacing-sm);
  
  &.form-group--grid & {
    gap: var(--spacing-sm);
  }
}

.form-group--relaxed .group-content {
  gap: var(--spacing-xl);
  
  &.form-group--grid & {
    gap: var(--spacing-xl);
  }
}

// Grid columns
.form-group--grid .group-content {
  grid-template-columns: repeat(v-bind('props.columns'), 1fr);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.group-errors {
  margin-top: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.group-error {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--vscode-inputValidation-errorForeground);
  background-color: var(--vscode-inputValidation-errorBackground);
  border: 1px solid var(--vscode-inputValidation-errorBorder);
  border-radius: var(--border-radius);
  padding: var(--spacing-sm) var(--spacing-md);
  line-height: 1.4;
}

.group-help {
  margin-top: var(--spacing-md);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  font-size: 12px;
  color: var(--vscode-foreground);
  opacity: 0.7;
  line-height: 1.4;
}

.help-icon {
  color: var(--vscode-settings-inlineActionsForeground);
  flex-shrink: 0;
  margin-top: 1px; // Align with text baseline
}

.error-icon {
  color: var(--vscode-inputValidation-errorForeground);
  flex-shrink: 0;
  margin-top: 1px; // Align with text baseline
}

// Responsive adjustments
@media (max-width: 768px) {
  .form-group--horizontal .group-content {
    flex-direction: column;
    
    > :deep(*) {
      min-width: auto;
    }
  }
}
</style>
